/**
 * @description
 * Base API client class providing common HTTP request functionality.
 * All service modules extend this class to inherit consistent request handling.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { getAuthToken } from "../../../lib/auth";

/**
 * Base API client class for backend communication
 */
export abstract class BaseAPIClient {
  protected baseURL: string;

  constructor() {
    // In production, use relative URLs since API is served from same domain
    // In development, use the configured API URL or default to localhost
    if (import.meta.env.PROD) {
      this.baseURL = "/api";
    } else {
      this.baseURL =
        import.meta.env.VITE_API_URL || "http://localhost:8788/api";
    }
  }

  /**
   * Make an authenticated request to the API
   */
  protected async request<T>(
    endpoint: string,
    options?: RequestInit,
  ): Promise<T> {
    const token = getAuthToken();
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options?.headers,
    };

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = (await response.json().catch(() => ({
        message: "Request failed",
      }))) as { message?: string };
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }
}
